/**
 * CTA Button Component
 *
 * Specialized button for Call-to-Action scenarios (Submit, Save, Create, Run, etc.)
 * Uses the shared neutral primary action color.
 *
 * Usage:
 *   <CTAButton type="submit">Submit Form</CTAButton>
 *   <CTAButton isLoading>Saving...</CTAButton>
 *   <CTAButton size="lg">Get Started</CTAButton>
 */

import * as React from 'react';
import { Button, ButtonProps } from './button';
import { cn } from '@/lib/utils';

interface CTAButtonProps extends Omit<ButtonProps, 'variant'> {
  /**
   * Override variant for special cases (defaults to primary action styling).
   */
  variant?: ButtonProps['variant'];
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, variant = 'primary', children, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant={variant}
        className={cn(
          'bg-[var(--mb-primary)] hover:bg-[var(--mb-primary-hover)] text-white shadow-[var(--mb-primary-shadow)]',
          'focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2',
          className
        )}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
CTAButton.displayName = 'CTAButton';

export { CTAButton };
